import React, { useState } from 'react';
import useApi from '../hooks/useApi';

export default function AllBooksPage({ books = [], isLoading = false, onUpdateBooks }) {
  const { request } = useApi();
  const [allBooks, setAllBooks] = useState(books);
  const [loading, setLoading] = useState(isLoading);
  const booksPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(allBooks.length / booksPerPage);
  const startIndex = (currentPage - 1) * booksPerPage;
  const currentBooks = allBooks.slice(startIndex, startIndex + booksPerPage);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPage = (page) => setCurrentPage(page);

  const fetchNextSetBooks = async (start, end) => {
    try {
      const data = await request({
        method: 'get',
        url: 'api/v1/pdf_parser/public_domain_books/',
        params: { start, end },
      });

      if (data?.public_books?.length) {
        setAllBooks(prev => {
          const updatedList = [...prev, ...data.public_books];
          onUpdateBooks(updatedList);
          return updatedList;
        });
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-10 bg-white text-gray-900">
      <h1 className="text-3xl font-bold text-center mb-10">All Public Domain Books</h1>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : currentBooks.length === 0 ? (
        <div className="text-center text-gray-600 mt-12">
          <p>No public domain books available at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {currentBooks.map(book => (
            <div
              key={book.book_meta_unique_id}
              className="cursor-pointer flex flex-col items-center group"
              onClick={() => window.location.href = `/read_book/${book.book_meta_unique_id}`}
            >
              <img
                src={book.book_cover}
                alt={book.book_name}
                className="w-full h-full object-cover rounded-lg shadow-md transition-transform group-hover:scale-105"
              />
              <p className="mt-2 text-sm font-medium text-gray-800 text-center line-clamp-2">
                {book.book_name}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-10 flex justify-center items-center space-x-2">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => goToPage(i + 1)}
              className={`px-3 py-1 border rounded ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => {
              const start = allBooks.length;
              const end = start + booksPerPage;
              setLoading(true);
              fetchNextSetBooks(start, end);
              goToNextPage();
            }}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
